import type { ReactNode } from 'react';
import { ChevronRight, LayoutGrid, Share2, Trophy, Users, Zap } from '../../components/ui/icons';
import { Sheet } from '../../components/ui/Sheet';

// ── Competition overflow menu (the top-right "⋯") ───────────────────────────────
export function LeagueMenuSheet({
  open,
  leagueName,
  isCycling,
  roundOpen,
  hasEntry,
  onClose,
  onInvite,
  onMiniLeagues,
  onStandings,
  onBuildCards,
  onLiveBoard
}: {
  open: boolean;
  leagueName: string;
  isCycling: boolean;
  roundOpen: boolean;
  hasEntry: boolean;
  onClose: () => void;
  onInvite: () => void;
  onMiniLeagues: () => void;
  onStandings: () => void;
  onBuildCards: () => void;
  onLiveBoard: () => void;
}) {
  const playLabel = isCycling ? 'Call your duels' : 'Build your cards';
  const playSub = roundOpen
    ? isCycling
      ? 'Rank the six rider duels on this stage'
      : 'Stack boosts on any fixtures you fancy'
    : 'Opens when the next slate is live';

  return (
    <Sheet open={open} onClose={onClose} ariaLabel="Competition options">
      <div className="px-5 pb-1 pt-2">
        <h2 className="truncate text-[18px] font-extrabold tracking-[-0.02em] text-ink">{leagueName}</h2>
        <p className="mt-0.5 text-[12px] font-semibold text-ink2">Competition options</p>
      </div>
      <div className="px-3 pb-3 pt-1">
        <LeagueMenuRow
          icon={<Zap width={19} height={19} strokeWidth={2} />}
          label={playLabel}
          sub={playSub}
          onClick={onBuildCards}
          disabled={!roundOpen}
        />
        {hasEntry && (
          <LeagueMenuRow
            icon={<LayoutGrid width={19} height={19} strokeWidth={2} />}
            label={isCycling ? 'Live stage board' : 'Live card board'}
            sub="Follow your points as results land"
            onClick={onLiveBoard}
          />
        )}
        <LeagueMenuRow
          icon={<Trophy width={19} height={19} strokeWidth={2} />}
          label="Division standings"
          sub="Your rank, season history and ladder"
          onClick={onStandings}
        />
        <LeagueMenuRow
          icon={<Share2 width={19} height={19} strokeWidth={2} />}
          label="Invite friends"
          sub="Share this competition"
          onClick={onInvite}
        />
        <LeagueMenuRow
          icon={<Users width={19} height={19} strokeWidth={2} />}
          label="Mini leagues"
          sub="Create or join a private table"
          onClick={onMiniLeagues}
        />
      </div>
    </Sheet>
  );
}

function LeagueMenuRow({
  icon,
  label,
  sub,
  onClick,
  disabled
}: {
  icon: ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="tap flex w-full items-center gap-3 rounded-[16px] px-3 py-[11px] text-left active:bg-fill disabled:opacity-45"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-fill text-ink">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-bold tracking-[-0.01em] text-ink">{label}</span>
        <span className="block text-[12px] font-medium text-ink2">{sub}</span>
      </span>
      <ChevronRight width={18} height={18} strokeWidth={2.4} className="text-ink4" />
    </button>
  );
}
